﻿/**
 * The function "trackConversion" returns a function that creates a conversion event object with the
 * given name and value.
 * @param name - The name parameter is a string that represents the name of the conversion event. It
 * could be something like "purchase", "signup", or "add_to_cart".
 * @param value - The "value" parameter is the value associated with the conversion event. It could be
 * a numeric value representing the amount of the conversion, or any other relevant information related
 * to the conversion.
 * @returns A function is being returned.
 */
function trackConversion(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      type: 'conversionEvent',
      name: event.name,
      value: event.value,
    };
  };
}

/**
 * The markStart function returns a function that creates an object with a type of 'markStart' and a
 * given name.
 * This function is very useful to mark the initial point when a task begin and the measure.
 * @param name - The `name` parameter is a string that represents the name of the action being
 * dispatched.
 * @returns A function is being returned.
 */
function markStart(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      type: 'markStart',
      name: event.name,
    };
  };
}

/**
 * The function `markEnd` returns a function that creates an object with properties `type`,
 * `measureName`, `markNameStart`, and `markNameEnd`.
 * This function is very useful to mark the end point when a task finish and the measure.
 * @param measureName - The name of the measure that the mark belongs to.
 * @param markNameStart - The parameter `markNameStart` is the name of the starting mark, it is created by markStart.
 * If the markstart is not created it will throw an error.
 * @param markNameEnd - The `markNameEnd` parameter is the name of the ending mark. It is used to
 * identify the end of a specific section or measurement in the code.
 * @returns an arrow function that returns an object with the properties `type`, `measureName`,
 * `markNameStart`, and `markNameEnd`.
 */
function markEnd(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      type: 'markEnd',
      measureName: event.measureName,
      markNameStart: event.markNameStart,
      markNameEnd: event.markNameEnd,
    };
  };
}

/**
 * The timingMetric function returns a function that creates an object with a type, name, and value
 * property.
 * @param name - The name parameter is a string that represents the name of the timing metric. It is
 * used to identify the specific metric being measured.
 * @param value - The value parameter is the value of the timing metric that you want to track. It
 * could be a number representing the time in milliseconds, seconds, or any other unit of time.
 * @returns an arrow function that returns an object with properties `type`, `name`, and `value`.
 */
function timingMetric(eventDefinition) {
  return (action, previousState, nextState) => {
    const event = eventDefinition(action, previousState, nextState);
    return {
      type: 'timingMetric',
      name: event.name,
      value: event.value,
    };
  };
}

export { trackConversion, markStart, markEnd, timingMetric };
